import type { StepAccessEntry } from './portal';
import type { NormalStep } from './steps.types';

/**
 * Returns true when the step should render read-only (all inputs disabled,
 * no Save & Continue). Driven exclusively by the step-access contract —
 * the portal never computes this from workflow, journey, or policy data.
 */
export const isReadOnly = (s: NormalStep) =>
  s.canApplicantEdit !== undefined
    ? !s.canApplicantEdit
    : s.accessLevel === 'view_only' || s.accessLevel === 'locked';

/** Globus submit capability is authoritative when present. Older snapshots
 * retain the access-level fallback so a rolling deployment does not remove
 * every applicant's submit button. */
export const canSubmitStep = (s: NormalStep) =>
  s.canApplicantSubmit !== undefined ? s.canApplicantSubmit : !isReadOnly(s);

/**
 * Merges a step-access snapshot into a normalised step array:
 * • `hidden` steps are filtered out of navigation entirely.
 * • `locked`, `view_only`, and `editable` steps are annotated with their
 *   access level, authored banner message, and scheduled-unlock timestamp.
 * Steps absent from the snapshot are left as-is (treated as `editable`).
 *
 * This is the ONLY place in the portal that reads the step-access contract.
 * No other module may derive access level or lock state from any other source.
 */
export function applyStepAccess(
  steps: NormalStep[],
  access: StepAccessEntry[],
): NormalStep[] {
  const map = new Map(access.map((e) => [e.stepKey, e]));
  return steps
    .filter((s) => (map.get(s.stepKey)?.accessLevel ?? 'editable') !== 'hidden')
    .map((s) => {
      const entry = map.get(s.stepKey);
      if (!entry) return s;
      return {
        ...s,
        accessLevel: entry.accessLevel,
        lockBannerMessage: entry.bannerMessage,
        lockReason: entry.reason,
        unlockAt: entry.unlock_at,
        needsAction: entry.needsAction,
        rejectionMessage: entry.rejectionMessage,
        rejectionCode: entry.rejectionCode,
        documentReviewStatus: entry.documentReviewStatus,
        canApplicantEdit: entry.canApplicantEdit,
        canApplicantSubmit: entry.canApplicantSubmit,
        unlockConditions: entry.unlockConditions,
        reviewItems: entry.reviewItems,
        reviewFeedback: entry.reviewFeedback,
      };
    });
}
